import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getCookie } from '../Utils/Cookies';
import { getUserLoginToken, getUser, getUserId, getCustomerId, getUserName, getUserEmailAddress, getFirstName, getLastName, removeAllCookies } from '../Utils/UserInfo';
import { TestAuthorization, GetCredentials, ClearCredentials } from '../services/AccountServices';
import useHttp from '../Network/Use-http/UseHttp';
import { config } from '../Utils/environment';
import { setUserId, clearUserId } from '../Utils/analytics';
import { markSessionActive, getSessionExpiry, getSessionEndReason } from '../Utils/sessionState';
import { handleSessionExpired, hasSessionEnded, SIGNED_OUT_ELSEWHERE_MESSAGE } from '../Utils/sessionExpiry';

const AuthContext = createContext(null);
const SESSION_CHECK_INTERVAL_MS = 20000;

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [, , CallApi] = useHttp();
    
    // Use ref to store CallApi function to prevent infinite loop
    // CallApi changes on every render, but we need stable reference for validateToken
    const callApiRef = useRef(CallApi);
    useEffect(() => {
        callApiRef.current = CallApi;
    }, [CallApi]);

    // GA4 user_id: the internal user id while signed in (never the email); cleared on logout.
    const analyticsUserId = user?.userId;
    useEffect(() => {
        if (isAuthenticated && analyticsUserId) setUserId(analyticsUserId);
        else clearUserId();
    }, [isAuthenticated, analyticsUserId]);

    // Handle logout
    const handleLogout = useCallback(() => {
        removeAllCookies();
        setIsAuthenticated(false);
        setUser(null);
        // Dispatch logout event for other components
        window.dispatchEvent(new Event('userLoggedOut'));
    }, []);

    // Validate token by calling backend
    // skipLoadingState: if true, don't set loading state (for background validation after optimistic update)
    const validateToken = useCallback((skipLoadingState = false) => {
        const token = getCookie('persistence-key');
        
        // If no token exists, user is not authenticated
        if (!token) {
            setIsAuthenticated(false);
            setIsAuthLoading(false);
            setUser(null);
            return;
        }

        // Only set loading state if not skipping (prevents button blink during background validation)
        if (!skipLoadingState) {
            setIsAuthLoading(true);
        }

        // Call backend to validate token - use ref to avoid dependency on CallApi
        TestAuthorization(callApiRef.current, (data) => {
            try {
                const response = data;
                if (response && response.status && response.data) {
                    // Token is valid - set authenticated state
                    const userData = getUser();
                    setIsAuthenticated(true);
                    setUser({
                        userId: getUserId(),
                        customerId: getCustomerId(),
                        userName: getUserName(),
                        emailAddress: getUserEmailAddress(),
                        firstName: getFirstName(),
                        lastName: getLastName(),
                        ...(userData && typeof userData === 'object' ? userData : {})
                    });
                } else {
                    // Token validation failed
                    handleLogout();
                }
            } catch (error) {
                console.error('Token validation error:', error);
                handleLogout();
            } finally {
                setIsAuthLoading(false);
            }
        }, (error) => {
            // API call failed - token is invalid or expired
            console.error('Token validation failed:', error);
            handleLogout();
            setIsAuthLoading(false);
        });
    }, [handleLogout]); // Removed CallApi from dependencies - using ref instead

    // Handle login success
    const handleLogin = useCallback(() => {
        // Just re-validate token after login
        // Don't dispatch event here - LoginModal already dispatches 'userLoggedIn' event
        validateToken();
    }, [validateToken]);

    // Validate token on mount and when cookies change
    useEffect(() => {
        setIsAuthLoading(true);
        validateToken();
    }, [validateToken]);

    // Listen for login/logout events from other components
    useEffect(() => {
        const handleUserLogin = () => {
            // Optimistically update auth state immediately (cookies are already set)
            // This prevents button from showing "Login" during validation
            const token = getCookie('persistence-key');
            if (token) {
                // Immediately update state from cookies (optimistic update)
                // Explicitly set loading to false to ensure button shows immediately
                setIsAuthLoading(false);
                setIsAuthenticated(true);
                setUser({
                    userId: getUserId(),
                    customerId: getCustomerId(),
                    userName: getUserName(),
                    emailAddress: getUserEmailAddress(),
                    firstName: getFirstName(),
                    lastName: getLastName(),
                    ...(getUser() && typeof getUser() === 'object' ? getUser() : {})
                });
            }
            
            // Then validate in background (will correct if token is invalid)
            // Pass skipLoadingState=true to prevent button blink during validation
            // Small delay to ensure cookies are fully set
            setTimeout(() => {
                validateToken(true); // Skip loading state to prevent UI flicker
            }, 100);
        };

        const handleUserLogout = () => {
            // state only: handleLogout itself dispatches 'userLoggedOut', calling it from this
            // listener would re-dispatch the event forever (stack overflow)
            // null: keep the end reason already recorded (a session expiry must not turn into "logout")
            removeAllCookies(null);
            setIsAuthenticated(false);
            setUser(null);
        };

        window.addEventListener('userLoggedIn', handleUserLogin);
        window.addEventListener('userLoggedOut', handleUserLogout);

        return () => {
            window.removeEventListener('userLoggedIn', handleUserLogin);
            window.removeEventListener('userLoggedOut', handleUserLogout);
        };
    }, [validateToken, handleLogout]);

    // Session-expiry watcher: login cookies expire (30 min, or 1 day with "Remember me") but
    // nothing else tells this context. While authenticated, a timer set to the stored expiry time
    // (sessionState) plus a cookie-presence check (no backend call) on an interval, on window focus
    // and when the tab becomes visible. Any of them finding the session over runs
    // handleSessionExpired: clear the login, toast once, redirect to /Account/Login?returnUrl=...
    // The timer and listeners are removed on logout (isAuthenticated flips to false).
    useEffect(() => {
        if (!isAuthenticated) return undefined;
        markSessionActive();

        const checkSession = () => {
            if (hasSessionEnded()) handleSessionExpired();
        };
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') checkSession();
        };

        let timeoutId = null;
        const armTimer = () => {
            if (timeoutId) clearTimeout(timeoutId);
            const expiresAt = getSessionExpiry();
            // setTimeout caps at ~24.8 days; logins last at most 1 day, the interval covers the rest
            timeoutId = expiresAt
                ? setTimeout(checkSession, Math.max(expiresAt - Date.now(), 0) + 50)
                : null;
        };
        armTimer();

        // Another tab changed the shared session: a new expiry (login / Remember me) re-arms the
        // timer; a removed expiry means that tab ended the session, so follow it.
        const handleStorage = (event) => {
            if (event.key !== 'session-expires-at') return;
            try {
                if (event.newValue) { armTimer(); return; }
                if (getCookie('persistence-key') || getUserLoginToken()) return; // still signed in
                if (getSessionEndReason() === 'logout') handleSessionExpired(SIGNED_OUT_ELSEWHERE_MESSAGE, 'logout');
                else handleSessionExpired();
            } catch { /* storage blocked: the interval and focus checks still run */ }
        };
        window.addEventListener('storage', handleStorage);
        const intervalId = setInterval(checkSession, SESSION_CHECK_INTERVAL_MS);
        window.addEventListener('focus', checkSession);
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            clearInterval(intervalId);
            window.removeEventListener('focus', checkSession);
            window.removeEventListener('storage', handleStorage);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [isAuthenticated]);

    //#region RedirectToPage - redirect to .NET page with pre-authentication

    /** Redirects to the .NET page with pre-authentication.
     * @param {string} redirectUrl - The URL to redirect to after credentials are retrieved.
     * @param {{ newTab?: boolean }} [options] - newTab opens the .NET page in a new tab and leaves
     *   this one where it is (the Work Queue uses it). The tab is opened here, synchronously in the
     *   click, so popup blockers allow it; the sign-in form posts into it once credentials arrive.
     */
    const RedirectToPage = (redirectUrl, options = {}) => {
        const target = options.newTab ? `athos-net-${Date.now()}` : null;
        if (target) window.open('about:blank', target);
        try {
            // Get credentials from server (will try sessionStorage backup if server fails)
            GetCredentials(callApiRef.current,
                // use curried function to pass redirectUrl to getCredentialsResponse
                (respData) => getCredentialsResponse(respData, redirectUrl, target),
                // Pass redirectUrl to error handler for fallback redirect
                (error) => getCredentialsError(error, redirectUrl, target)
            );
        } catch (error) {
            console.error('Error in RedirectToPage:', error);
            // Fallback to direct redirect if something goes wrong
            getCredentialsError(error, redirectUrl, target);
        }
    };
    /** Handles successful response from GetCredentials.
     * @param {{status: boolean, data: {userName: string, password: string}}} responseData - The response data from the GetCredentials call.
     * @param {string} redirectUrl - The URL to redirect to after credentials are retrieved.
     */
    const getCredentialsResponse = (responseData, redirectUrl, target = null) => {
        const response = responseData;
        try {
            if (response && response.status && response.data) {
                const { userName, password } = response.data;
                try {
                    // Create a form to POST to the .NET login endpoint
                    const form = document.createElement('form');
                    form.method = 'POST';
                    form.action = `${config.ATHOS_DOT_NET_URL}/Account/Logon?returnUrl=${encodeURIComponent(redirectUrl)}`;

                    form.style.display = 'none';
                    if (target) form.target = target;

                    // Add the login credentials as form fields
                    const usernameField = document.createElement('input');
                    usernameField.type = 'hidden';
                    usernameField.name = 'UserName';
                    usernameField.value = userName;
                    form.appendChild(usernameField);

                    const passwordField = document.createElement('input');
                    passwordField.type = 'hidden';
                    passwordField.name = 'Password';
                    passwordField.value = password;
                    form.appendChild(passwordField);

                    const rememberMeField = document.createElement('input');
                    rememberMeField.type = 'hidden';
                    rememberMeField.name = 'RememberMe';
                    rememberMeField.value = 'false';
                    form.appendChild(rememberMeField);

                    // Submit the form
                    document.body.appendChild(form);
                    form.submit();

                    // Clean up the form after submission
                    setTimeout(() => {
                        if (document.body.contains(form)) {
                            document.body.removeChild(form);
                        }
                        // Clear credentials from server after successful redirect
                        ClearCredentials(callApiRef.current, clearCredentialsResponse, clearCredentialsError);
                    }, 1000);
                } catch (formError) {
                    console.error('Form POST failed:', formError);
                }
            } else {
                console.error('Failed to retrieve credentials:', response?.message);
                // Fallback to direct redirect without pre-authentication
                getCredentialsError('Failed to retrieve credentials', redirectUrl, target);
            }
        } catch (error) {
            console.error('Error retrieving credentials:', error);
            // Fallback to direct redirect without pre-authentication
            getCredentialsError(error, redirectUrl, target);
        }
    };

    const clearCredentialsResponse = (responseData) => {
        const response = responseData;
        if (!(response && response.status && response.data)) {
            console.error('Failed to clear credentials:', response?.message);
        }
    };
    const clearCredentialsError = (error) => {
        console.error('Error clearing credentials:', error);
    };

    /** Handles error when credentials cannot be retrieved.
     * Falls back to direct redirect to .NET login page where user can login manually.
     */
    const getCredentialsError = (error, redirectUrl, target = null) => {
        console.error('Error getting credentials:', error);
        // Fallback: Redirect to .NET login page without pre-authentication
        // User will need to login manually, but they'll be redirected to the intended page after login
        try {
            const loginUrl = `${config.ATHOS_DOT_NET_URL}/Account/Logon?returnUrl=${encodeURIComponent(redirectUrl || '/Dashboard')}`;
            // new-tab mode: send the tab opened for it, never this page
            if (target) { window.open(loginUrl, target); return; }
            window.location.href = loginUrl;
        } catch (redirectError) {
            console.error('Error redirecting to .NET site:', redirectError);
            // Last resort: redirect to dashboard
            window.location.href = `${config.ATHOS_DOT_NET_URL}/Dashboard`;
        }
    };
    //#endregion

    const value = {
        isAuthenticated,
        isAuthLoading,
        user,
        login: handleLogin,
        logout: handleLogout,
        refreshAuth: validateToken,
        RedirectToPage,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
